import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import BookDemoModal from './BookDemoModal';

const POPUP_DELAY_MS = 5000;
const MAX_POPS = 2;

// Attention popup: fires 5s after the visitor lands, and — if dismissed —
// once more 5s after they start scrolling. "Join Now" hands off to
// BookDemoModal (name / email / phone / course form) and stops re-pops.
export default function FreeDemoPopup() {
  const location = useLocation();
  const [showTeaser, setShowTeaser] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const popsRef = useRef(0);
  const engagedRef = useRef(false);

  useEffect(() => {
    // Program pages auto-open their own course lead popup at 5s — don't
    // stack a second popup on top of it. Admin has no marketing UI.
    if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/programs')) return;
    if (showTeaser || showForm || engagedRef.current || popsRef.current >= MAX_POPS) return;

    let timer = null;
    const pop = () => {
      popsRef.current += 1;
      setShowTeaser(true);
    };

    // First pop: fixed delay after landing.
    if (popsRef.current === 0) {
      timer = setTimeout(pop, POPUP_DELAY_MS);
      return () => clearTimeout(timer);
    }

    // Second pop: wait for the visitor to scroll, then re-pop after the
    // same delay.
    const onScroll = () => {
      window.removeEventListener('scroll', onScroll);
      timer = setTimeout(pop, POPUP_DELAY_MS);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (timer) clearTimeout(timer);
    };
  }, [location.pathname, showTeaser, showForm]);

  useEffect(() => {
    if (!showTeaser) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setShowTeaser(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [showTeaser]);

  if (location.pathname.startsWith('/admin')) return null;

  const handleJoinNow = () => {
    engagedRef.current = true;
    setShowTeaser(false);
    setShowForm(true);
  };

  return (
    <>
      {showTeaser && createPortal(
        <div
          data-testid="free-demo-popup"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F1F3D]/60 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setShowTeaser(false); }}
        >
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg px-6 py-10 sm:px-12 text-center">
            <button
              onClick={() => setShowTeaser(false)}
              data-testid="free-demo-popup-close"
              aria-label="Close"
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#F0F0F0] hover:bg-[#E0E0E0] flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-[#585858]" />
            </button>

            <h2 className="font-heading font-black text-2xl sm:text-3xl leading-snug text-[#123C7A]">
              Why Leave? You Can Attend a Free Demo!
            </h2>
            <p className="font-body text-sm sm:text-base text-[#585858] mt-4 max-w-md mx-auto">
              Don&rsquo;t miss out! Join our free demo session and kickstart your
              career with A7 Globetech.
            </p>

            <button
              onClick={handleJoinNow}
              data-testid="free-demo-popup-join-btn"
              className="mt-7 font-heading font-bold text-lg text-white bg-[#123C7A] hover:bg-[#0F1F3D] px-10 py-3 rounded-full transition-colors"
            >
              Join Now
            </button>
          </div>
        </div>,
        document.body
      )}

      {showForm && <BookDemoModal onClose={() => setShowForm(false)} />}
    </>
  );
}
